import { useState } from 'react';
import { clsx } from 'clsx';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

import { toast } from '../../Toast';
import { Info } from '../../Info/Info';
import { useMasterDocumentFormatters } from '../formatters';

import { AdvanceStatusBadge } from './AdvanceStatusBadge';
import { ApplyAdvanceModal } from './ApplyAdvanceModal';
import { Loading } from '../components/loading';
import { useAdvancesTranslation } from './i18n';
import {
  useAvailableAdvances,
  useDocumentAdvanceApplications,
  useRemoveAdvance,
} from './hooks';
import type {
  AdvanceApplicationsPanelProps,
  AdvancesHttpError,
  DocumentAdvanceApplicationDto,
} from './types';

/**
 * Panel de aplicaciones de anticipo — sub-componente de MasterDocument (FEAT-102-5).
 *
 * Renderiza las aplicaciones de anticipo del documento (monto, referencia del
 * saldo origen, `Badge` de estado) y, cuando el documento es editable (DRAFT),
 * permite aplicar nuevos anticipos y quitar los `RESERVED`.
 *
 * Diseñado para montarse en una sección FactBox de cualquier documento
 * MasterDocument, inyectando sus endpoints e IO por props (CA-I1 / CA-I4).
 *
 * @example
 * ```tsx
 * <AdvanceApplicationsPanel
 *   documentId={docId}
 *   supplierId={supplierId}
 *   documentCurrencyId={currencyId}
 *   isEditable={docState === 'DRAFT'}
 *   applyAdvanceEndpoint={`/api/v1/documents/${docId}/advances`}
 *   removeAdvanceEndpoint={`/api/v1/documents/${docId}/advances`}
 *   availableAdvancesEndpoint={`/api/v1/documents/${docId}/available-advances`}
 *   httpRequest={httpRequest}
 *   fetcher={advancesFetcher}
 * />
 * ```
 */
export function AdvanceApplicationsPanel({
  documentId,
  documentCurrencyId,
  isEditable,
  applyAdvanceEndpoint,
  removeAdvanceEndpoint,
  availableAdvancesEndpoint,
  applicationsEndpoint,
  httpRequest,
  fetcher,
  searchEntity = 'available-advances',
  formatAmount: formatAmountProp,
  className,
}: AdvanceApplicationsPanelProps) {
  const { t } = useAdvancesTranslation();
  const { formatAmount: defaultFormatAmount, formatDateTime } = useMasterDocumentFormatters();
  const formatAmount = formatAmountProp ?? defaultFormatAmount;
  const [modalOpen, setModalOpen] = useState(false);

  /**
   * `undefined` cuando no hay fecha o es inválida, para que
   * `AdvanceStatusBadge` no muestre un tooltip vacío (preserva el
   * comportamiento previo; `formatDateTime` del hook devolvería `'—'`).
   */
  function formatDate(iso?: string | null): string | undefined {
    if (!iso) return undefined;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return undefined;
    return formatDateTime(iso);
  }

  /** Selecciona la fecha del tooltip según el estado (§4.3). */
  function tooltipDate(app: DocumentAdvanceApplicationDto): string | undefined {
    switch (app.status) {
      case 'CONFIRMED':
        return formatDate(app.confirmedAt);
      case 'REVERSED':
        return formatDate(app.reversedAt);
      default:
        return formatDate(app.reservedAt);
    }
  }

  const applicationsQuery = useDocumentAdvanceApplications({
    documentId,
    applicationsEndpoint: applicationsEndpoint ?? applyAdvanceEndpoint,
    httpRequest,
  });

  // Saldos disponibles: alimenta el contador y se invalida tras aplicar/quitar.
  const availableQuery = useAvailableAdvances({
    documentId,
    availableAdvancesEndpoint,
    httpRequest,
    enabled: isEditable,
  });

  const removeMutation = useRemoveAdvance({ documentId, removeAdvanceEndpoint, httpRequest });

  const applications = applicationsQuery.data ?? [];
  const availableCount = availableQuery.data?.length ?? 0;

  function handleRemove(applicationId: string) {
    removeMutation.mutate(applicationId, {
      onSuccess: () => toast.success(t('advances.toast.removed')),
      onError: (error) => {
        const status = (error as AdvancesHttpError)?.status;
        if (status === 403) {
          toast.error(t('advances.error.forbidden'));
          return;
        }
        toast.error(error?.message || t('advances.error.generic'));
      },
    });
  }

  return (
    <section
      className={clsx('flex flex-col gap-3', className)}
      aria-label={t('advances.panel.title')}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-baseline gap-2 min-w-0">
          <h3 className="text-sm font-bold text-content-primary dark:text-dark-content-primary truncate">
            {t('advances.panel.title')}
          </h3>
          {isEditable && availableCount > 0 && (
            <span className="text-xs text-content-secondary dark:text-dark-content-secondary shrink-0">
              {t('advances.panel.available', { count: availableCount })}
            </span>
          )}
        </div>

        {/* Botón Aplicar — solo en DOM cuando isEditable (FD-04 / CA-F3) */}
        {isEditable && (
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className={clsx(
              'inline-flex items-center gap-1.5 shrink-0',
              'px-3 py-1.5 rounded-md text-xs font-semibold',
              'bg-primary-600 text-white',
              'hover:bg-primary-700 transition-colors duration-150',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2',
              'dark:bg-dark-bg-inverse dark:text-dark-content-inverse dark:hover:bg-dark-bg-inverse/90',
              'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
            )}
          >
            <PlusIcon className="w-4 h-4" aria-hidden="true" />
            {t('advances.panel.apply')}
          </button>
        )}
      </div>

      {/* Estados de carga / error */}
      {applicationsQuery.isLoading ? (
        <Loading size="md" label={t('advances.panel.loading')} />
      ) : applicationsQuery.isError ? (
        <Info color="red" title={t('advances.panel.loadError')} />
      ) : applications.length === 0 ? (
        <p className="text-sm text-content-secondary dark:text-dark-content-secondary py-3">
          {t('advances.panel.empty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {applications.map((app) => {
            const canRemove = isEditable && app.status === 'RESERVED';
            return (
              <li
                key={app.id}
                className={clsx(
                  'flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl',
                  'border border-border-primary dark:border-dark-border-primary',
                  'bg-bg-primary dark:bg-dark-bg-secondary',
                )}
              >
                {/* Monto + referencia */}
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-semibold text-content-primary dark:text-dark-content-primary">
                    {formatAmount(app.appliedAmount)}
                  </span>
                  <span className="text-xs text-content-secondary dark:text-dark-content-secondary truncate">
                    {app.sourceDocumentReference || app.openBalanceId}
                  </span>
                </div>

                {/* Estado + acción */}
                <div className="flex items-center gap-2 shrink-0">
                  <AdvanceStatusBadge
                    status={app.status}
                    statusLabel={app.statusLabel}
                    tooltip={tooltipDate(app)}
                  />
                  {canRemove && (
                    <button
                      type="button"
                      onClick={() => handleRemove(app.id)}
                      disabled={removeMutation.isPending}
                      className={clsx(
                        'inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium',
                        'text-red-600 dark:text-red-400',
                        'hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-150',
                        'focus:outline-hidden focus:ring-2 focus:ring-red-400 focus:ring-offset-2',
                        'dark:focus:ring-offset-dark-bg-primary',
                        'disabled:opacity-40 disabled:pointer-events-none',
                      )}
                      aria-label={t('advances.panel.removeAria')}
                    >
                      <TrashIcon className="w-3.5 h-3.5" aria-hidden="true" />
                      {t('advances.panel.remove')}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Modal de aplicación (solo se monta si isEditable) */}
      {isEditable && (
        <ApplyAdvanceModal
          open={modalOpen}
          documentId={documentId}
          documentCurrencyId={documentCurrencyId}
          applyAdvanceEndpoint={applyAdvanceEndpoint}
          httpRequest={httpRequest}
          fetcher={fetcher}
          searchEntity={searchEntity}
          formatAmount={formatAmount}
          onClose={() => setModalOpen(false)}
        />
      )}
    </section>
  );
}
